import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../api'

/**
 * The terminal's data hooks, in one place.
 *
 * <p>Every one of these carries `placeholderData: (previous) => previous`, which is what keeps a
 * figure on screen while its refetch is in flight instead of blinking back to a spinner on every
 * account switch. Collected here so that the query keys can be read as a set - two panels sharing
 * a key share a cache entry and make one request between them, and that is only visible when they
 * are written next to each other.
 */

export function useNetWorth(accountId: string) {
  return useQuery({
    queryKey: ['net-worth', accountId],
    queryFn: () => api.netWorth(accountId),
    placeholderData: (previous) => previous,
  })
}

export function useRegime(accountId: string) {
  return useQuery({
    queryKey: ['regime', accountId],
    queryFn: () => api.regime(accountId),
    placeholderData: (previous) => previous,
  })
}

export function useAllocation(accountId: string) {
  return useQuery({
    queryKey: ['allocation', 'ASSET_CLASS', accountId],
    queryFn: () => api.allocation({ dimension: 'ASSET_CLASS', accountId }),
    placeholderData: (previous) => previous,
  })
}

export function useHoldings(accountId: string) {
  return useQuery({
    queryKey: ['holdings', accountId],
    queryFn: () => api.holdings(accountId),
    placeholderData: (previous) => previous,
  })
}

/**
 * The 30-day move, off the snapshot series. A day the server could not value records no value at
 * all (FR-E2), so the ends of the window are the first and last *complete* snapshots in it -
 * comparing against an incomplete day would report a holding that lost its price as a loss.
 */
export function useThirtyDayChange(accountId: string) {
  const from = useMemo(() => isoDaysAgo(30), [])

  return useQuery({
    queryKey: ['snapshots', accountId, from],
    queryFn: async () => {
      const series = await api.snapshots(accountId, from)
      const valued = series.filter((s) => s.marketValue != null && s.complete)
      const opening = valued[0]
      const closing = valued[valued.length - 1]
      if (!opening || !closing || opening === closing) {
        return {
          percent: null,
          absolute: null,
          from,
          note: 'fewer than two complete snapshots in the last 30 days',
        }
      }
      const first = Number(opening.marketValue)
      const last = Number(closing.marketValue)
      if (first === 0) {
        return { percent: null, absolute: null, from, note: 'the window opens at zero' }
      }
      return {
        percent: (((last - first) / first) * 100).toFixed(2),
        absolute: (last - first).toFixed(2),
        from: opening.date,
        note: null,
      }
    },
  })
}

export function isoDaysAgo(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString().slice(0, 10)
}

/**
 * The account list, which eight screens ask for independently.
 *
 * <p>They already shared one cache entry and one request - TanStack dedupes by key, and the key
 * was identical in all eight - so collapsing them changes no behaviour at runtime. What it
 * changes is that the key and the fetcher are now written once: eight copies of a literal
 * `['accounts']` is eight places for the next one to be spelled differently, and a key that
 * differs by a character is a second cache entry and a second request that look exactly like the
 * first from the outside.
 */
export function useAccounts() {
  return useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.listAccounts(),
  })
}
